import { useWorkspaceStore } from '../../panels/use-workspace-store';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { QueueCreationForm } from './queue-creation-form';
import type { FormData } from './queue-creation-form';
import { PanelModal } from '../../common/components/panel-modal';
import { queueFilterEnum } from '../queue-filter-enum';
import type { AssignmentStatus } from '../types';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { Flex, ButtonGroup, Button } from '@cad-arc';
import { WorkspaceQueueDto, WorkspacesResource } from '@mark43/cad-api';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';

function formAssignmentStatusToActualAssignmentStatus(
    formAssignmentStatus: typeof queueFilterEnum[keyof typeof queueFilterEnum]['assignmentStatus']
): boolean | undefined {
    if (formAssignmentStatus === 'ALL') {
        return undefined;
    } else if (formAssignmentStatus === 'ASSIGNED') {
        return true;
    } else {
        return false;
    }
}

export function QueueCreationPanelModal({ panel }: { panel: WorkspacePanel }): React.ReactElement {
    const apiClient = useApiClient();
    const panelsStore = useWorkspaceStore();
    const methods = useForm<FormData>({
        defaultValues: { dataEntityType: 'CAD_AGENCY_EVENT', filters: [] },
    });
    const { getValues } = methods;

    const { isError, isLoading, mutate } = useMutation(
        (args: {
            dataEntityType: WorkspaceQueueDto['dataEntityType'];
            assignmentStatus: AssignmentStatus;
            queueClasses: WorkspaceQueueDto['queueClasses'];
            eventQueue: WorkspaceQueueDto['eventQueue'];
        }) => {
            const { dataEntityType, assignmentStatus, queueClasses, eventQueue } = args;
            return apiClient.request<WorkspacesResource.CreateQueueForWorkspace1>({
                url: `workspaces/window/${panelsStore.getActiveWorkspaceWindowId()}/queue/`,
                data: {
                    dataEntityType,
                    queueClasses,
                    assignmentStatus: formAssignmentStatusToActualAssignmentStatus(
                        assignmentStatus
                    ),
                    eventQueue,
                },
                method: 'POST',
            });
        },
        {
            onSuccess(response) {
                if (response.data.data) {
                    const workspacePanel = response.data.data;
                    panel.close();
                    if (workspacePanel.queue) {
                        panelsStore.addPanel(
                            {
                                ...workspacePanel,
                                xcoordinate: panel.xcoordinate,
                                ycoordinate: panel.ycoordinate,
                                width: panel.width,
                                height: panel.height,
                            },
                            true
                        );
                    }
                }
            },
        }
    );

    const onSubmit = useCallback(() => {
        const { dataEntityType, filters } = getValues();
        const eventQueue: WorkspaceQueueDto['eventQueue'] =
            dataEntityType === 'TRU_EVENT' ? 'TELEPHONE_RESPONSE_UNIT' : 'DISPATCH';
        const entityType = dataEntityType === 'TRU_EVENT' ? 'CAD_AGENCY_EVENT' : dataEntityType;
        const queueClasses = filters.map((v) => queueFilterEnum[v]).map((v) => v.queueClass);
        // assume all queue filters have the same assignmentStatus, use first
        const assignmentStatus =
            filters.length > 0 ? queueFilterEnum[filters[0]].assignmentStatus : 'ALL';
        mutate({ dataEntityType: entityType, assignmentStatus, queueClasses, eventQueue });
    }, [getValues, mutate]);

    return (
        <FormProvider {...methods}>
            <PanelModal
                isCloseable={false}
                header="Create New Queue"
                body={<QueueCreationForm isError={isError} />}
                style={{ width: 540 }}
                footer={
                    <Flex justifyContent="flex-end">
                        <ButtonGroup>
                            <Button
                                variant="ghost"
                                onClick={() => panel.close()}
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="solid"
                                type="submit"
                                form="queue-creation-form"
                                onClick={onSubmit}
                                isLoading={isLoading}
                            >
                                Create
                            </Button>
                        </ButtonGroup>
                    </Flex>
                }
            />
        </FormProvider>
    );
}
